import React, { useState, useEffect, useRef, Suspense, lazy } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'

// Data Modules, CONSTANTS, Helpers
import { DATE_FIELDS, MODULE_FIELD_TYPES } from '../../../constants'
import { useRequest } from '../../../hooks/UseRequest'
import { handleErrorResponse, generateErrorBody } from '../../../helpers/errorHandling'
import { hasUserPermission } from '../../../utils/permissionHelper'
import { fetchWithTimeoutAndRetry } from '../../../utils/fetchHelper'

// REDUX
import { selectAuth } from "../../../redux/authSlice"
import { setSidebarIsActive } from "../../../redux/layoutSlice"
import { setModal, selectModal } from "../../../redux/modalSlice"
import { selectModules } from '../../../redux/moduleSlice'
import { selectDropdownOptions, selectDropdowns } from '../../../redux/dropdownSlice'
import { selectModuleData } from '../../../redux/moduleDataSlice'
import { selectUser } from '../../../redux/userSlice'
import { setNotification } from '../../../redux/notificationSlice'
import { selectSimulatedDate } from '../../../redux/simulatedDateSlice'
import { setPaymentScheduleCurrentDate } from '../../../redux/paymentScheduleDataSlice'

// Components
import LayoutSidebar from '../../../components/LayoutSidebar'
import Loading from '../../../components/Loading'
import SubheaderDivider from '../../../components/elements/SubheaderDivider'
import DisplayField from '../../../components/elements/DisplayField'
import PaymentScheduleSubPanel from "./PaymentScheduleSubPanel"
import dashletDetailViewControl from '../../dashlets/dashletDetailViewControl'
import NoRecord from '../../../components/elements/NoRecord'
import NoteButton from '../../../components/elements/NoteButton'
import CustomerServiceButtons from "../../../components/CustomerServiceButtons"
import LockNotice from '../../../components/elements/LockNotice'
import { useGetBanners } from "../../../utils/detailViewHelper";
import { FormatDateTimeToSame } from '../../../helpers/formatting'

const ModuleSubPanel = lazy(() => import('../../modules/ModuleSubPanel'));

/**
 * Main Detail page for all of LOX Admin. This page is controlled by the BE module system and renders all the fields,
 * the form and the sub panels dynamically based on that system.
 * NOTE: There is quite a bit of custom logic that is module specific. When adding new custom logic add a block comment
 *  specifying the code as CUSTOM and put the module name that is goes with. EX: MODULE: System Change Log
 * @returns {JSX.Element}
 * @constructor
 */
export default function PaymentSchedule() {
  let count = 0
  const {
    register, handleSubmit, control, setError, formState: { errors }, watch
  } = useForm()
  const [editMode, setEditMode] = useState(false)
  const [saving, setSaving] = useState(false)
  const [row, setRow] = useState()
  const [showAll, setShowAll] = useState(false)
  const [module, setModule] = useState()
  const [parentModule, setParentModule] = useState()
  const [moduleFields, setModuleFields] = useState()
  const [loanModuleFields, setLoanModuleFields] = useState()
  const [leadModuleFields, setLeadModuleFields] = useState()
  const [showMore, setShowMore] = useState(false)
  const [userPermissions, setUserPermissions] = useState()
  const requesting = useRef(false)
  const requests = useRequest()
  const requestsForm = useRequest()
  const params = useParams()
  const navi = useNavigate()
  const location = useLocation()
  const modules = useSelector(selectModules)
  const dropDowns = useSelector(selectDropdowns)
  const auth = useSelector(selectAuth)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const modal = useSelector(selectModal)
  const active = useSelector(selectUser)
  const moduleData = useSelector(selectModuleData)
  const dispatch = useDispatch()
  const [redirectedData, setRedirectedData] = useState(false)
  const [loanInfo, setLoanInfo] = useState()
  const [leadInfo, setLeadInfo] = useState()
  const [toggleSection, setToggleSection] = useState(
    {
      customer: true,
      lead: false,
      loan: false,
    }
  )
  const customerFields = ['guid', 'customer_status_dropdown_option_id', 'nonfundable_reason_dropdown_option_id', 'ssn', 'email_address', 'home_phone_number', 'cell_phone_number', 'birth_date', 'address', 'address2', 'city', 'state_dropdown_option_id', 'zip_code_id', 'portfolio_id', 'tags', 'employer_name', 'net_monthly_income', 'is_pay_before_holiday', 'bma_fields']
  const leadFields = ['lead_status_dropdown_option_id', 'is_express_loan', 'is_returning_customer', 'verification_action_source_dropdown_option_id', 'customer_promotion_code_id', 'tags', 'apr_standard_interval_dropdown_option_id', 'loan_funding_method_dropdown_option_id', 'payment_method_dropdown_option_id', 'origination_date']
  const loanFields = ['guid', 'loan_status_dropdown_option_id', 'loan_sub_status_dropdown_option_id', 'effective_date', 'principal_amount', 'interest_rate', 'apr', 'payment_amount', 'number_of_installments', 'first_payment_date', 'loan_agreement_id', 'first_missed_payment_date', 'program_id', 'pay_frequency_dropdown_option_id', 'refinanced_loan_id', 'tags']
  const [displayLockNotice, setDisplayLockNotice] = useState(false)
  const [lockNotificationText, setLockNotificationText] = useState()
  const [lockAgentUserId, setLockAgentUserId] = useState()
  const [forceRecordUnlockPermission, setForceRecordUnlockPermission] = useState(false)
  const [lockCheck, setLockCheck] = useState(false)
  const simulatedDate = useSelector(selectSimulatedDate)
  const customerId = params?.id
  const [currentDate, setCurrentDate] = useState(simulatedDate?.simulated_date ?? null)
  const queryParams = new URLSearchParams(location.search);

  // Sync local currentDate from global clock — this is the source of truth
  useEffect(() => {
    const globalDate = simulatedDate?.simulated_date ?? null
    setCurrentDate(globalDate)
    dispatch(setPaymentScheduleCurrentDate(globalDate))
  }, [simulatedDate?.simulated_date])

  const handleToggle = (section) => {
    setToggleSection((prevState) => ({
      ...prevState,
      [section]: !prevState[section]
    }));
  }

  const { getBanners, isLoadingBanners, banners } = useGetBanners();

  /**
   * Check if the user has correct permissions
   */
  useEffect(() => {
    if (active.status === 'succeeded' && module) {
      const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
      setUserPermissions(activePermissions)
      if (!hasUserPermission(activePermissions, `customer_read`)) {
        navi('/403')
      }
      if (hasUserPermission(activePermissions, `force_record_unlock`)) {
        setForceRecordUnlockPermission(true)
      }
    }
  }, [active, module])

  const calculateDisplayRank = (item, fields) => {
    return fields.indexOf(item)
  }

  /**
   * Check to ensure the requested module exists and store the data in local state
  */
  useEffect(() => {
    if (modules.data.length > 0) {
      const ps = modules.data.find((e) => e.code_key === 'payment_schedule')
      if (ps) {
        setModule(ps)
      }

      const m = modules.data.find((e) => e.code_key === 'customer')
      if (m) {
        setParentModule(m)
        // Sort Fields
        const mf = [...m.module_fields]
        mf.sort((a, b) => calculateDisplayRank(a.column_name, customerFields) - calculateDisplayRank(b.column_name, customerFields));
        setModuleFields(mf)
      } else {
        navi('/404')
      }

      const ld = modules.data.find((e) => e.code_key === 'lead')
      if (ld) {
        const ldf = [...ld.module_fields]
        ldf.sort((a, b) => calculateDisplayRank(a.column_name, leadFields) - calculateDisplayRank(b.column_name, leadFields));
        setLeadModuleFields(ldf)
      } else {
        navi('/404')
      }

      const ln = modules.data.find((e) => e.code_key === 'loan')
      if (ln) {
        const lnf = [...ln.module_fields]
        lnf.sort((a, b) => calculateDisplayRank(a.column_name, loanFields) - calculateDisplayRank(b.column_name, loanFields));
        setLoanModuleFields(lnf)
      } else {
        navi('/404')
      }
    }
  }, [modules.data, params.module_name])

  /**
   * Determine whether to open the Dashlet sidebar by default
   */
  useEffect(() => {
    if (module && module?.module_related_dashlets?.length > 0) {
      dispatch(setSidebarIsActive({ sidebarIsActive: true }))
    } else {
      dispatch(setSidebarIsActive({ sidebarIsActive: false }))
    }
  }, [module])

  /**
   * If redirecting back here after submitting a form show the form notification for success
   */
  useEffect(() => {
    if (modules && row && !redirectedData && location.state?.redirectedData) {
      setRedirectedData(location.state.redirectedData)
      window.history.replaceState({}, document.title)
      dispatch(setNotification({ type: 'success', body: redirectedData }))
    }
  }, [modules, row])

  const getCustomerDetail = async () => {
    // Gate parallel re-entry — the useEffect below has `auth` in its deps,
    // and an MSAL silent-refresh or page-transition recheck re-fires it.
    if (requesting.current) return
    requesting.current = true

    try {
      const loanGuid = queryParams.get('loan')
      setLoanInfo(null)
      setLeadInfo(null)
      const endpoint = loanGuid
        ? `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/loan/${loanGuid}/detail`
        : `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/detail`
      requests.setEndpoint(endpoint)

      // Mount-critical — see fetchHelper.js. Without this wrapper, a hung
      // or transient-401 request leaves `row` undefined and the page stuck.
      const response = await fetchWithTimeoutAndRetry(() => requests.get())
      setRow(response.data?.data?.customer)

      let tmpRow = response.data.data.customer
      if (tmpRow.bma_fields && Object.keys(tmpRow.bma_fields).length > 0) {
        if (tmpRow.bma_fields.first_name !== undefined) {
          tmpRow.full_name = tmpRow.bma_fields.first_name || "-"
        } else {
          tmpRow.full_name = row.full_name
        }
        if (tmpRow.bma_fields.email_address !== undefined) {
          tmpRow.email_address = tmpRow.bma_fields.email_address || "-"
        } else {
          tmpRow.email_address = row.email_address
        }
        if (tmpRow.bma_fields.state_dropdown_option_id !== undefined) {
          tmpRow.state_dropdown_option_id = tmpRow.bma_fields.state_dropdown_option_id || "-"
          tmpRow.city = "-"
        } else {
          tmpRow.state_dropdown_option_id = row.state_dropdown_option_id
        }
        if (tmpRow.bma_fields.home_phone_number !== undefined) {
          tmpRow.home_phone_number = tmpRow.bma_fields.home_phone_number || "-"
          tmpRow.cell_phone_number = "-"
        } else {
          tmpRow.home_phone_number = row.home_phone_number
        }
      }
      setRow(tmpRow)

      setLoanInfo(response.data?.data?.loan)
      setLeadInfo(response.data?.data?.lead)
      if (response.data?.data?.customer?.id) {
        getBanners('customer', response.data.data.customer.id)
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        navi('/404')
      } else {
        dispatch(setNotification({ type: 'error', body: error?.response?.data?.message ?? 'Failed to load customer data.' }))
      }
    } finally {
      requesting.current = false
    }
  }

  /**
   * Get Module Row Data
   */
  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (!requesting.current && module && auth.accessToken) {
      getCustomerDetail()
    }
  }, [module, moduleData, auth])

  // Display data filter created_at Order of IF's important
  const columnDataRender = (data, key, customFields, displayData) => {
    if (!customFields.includes(data.column_name)) return ''
    const fieldType = dropdownOptions[data.module_field_type_dropdown_option_id]?.code_key

    // Filter Columns/Types
    if (DATE_FIELDS.includes(data.column_name)
      || data.column_name === 'id'
      || fieldType === MODULE_FIELD_TYPES.SHOWMORE.code_key
      || fieldType === MODULE_FIELD_TYPES.SPECIAL_INSTRUCTIONS.code_key) {
      return ''
    }

    // Only Count the fields if they are rendered
    count++

    // If showMore option exists render all BEFORE showMore field
    if (showMore) {
      if (data.detail_display_rank < showMore.detail_display_rank || showAll) {
        // Display Sub header
        if (fieldType === MODULE_FIELD_TYPES.SUBHEADER.code_key) {
          return (
            <SubheaderDivider
              order={count}
              key={key}
              name={data.label_name}
            />
          )
        }
        // Or Render Data
        return (
          <DisplayField
            data={data}
            row={displayData}
            key={key}
            dropdowns={dropDowns}
            dropdownOptions={dropdownOptions}
          />
        )
      }
    } else {
      // Display Sub header for Non showMore module
      if (fieldType === MODULE_FIELD_TYPES.SUBHEADER.code_key) {
        return (
          <SubheaderDivider
            order={count}
            key={key}
            name={data.label_name}
          />
        )
      }
      // Or Render Data for Non showMore module
      return (
        <DisplayField
          data={data}
          row={displayData}
          key={key}
          dropdowns={dropDowns}
          dropdownOptions={dropdownOptions}
        />
      )
    }
    return ''
  }

  const dispatchErorr = () => {
    if (Object.keys(errors).length > 0) {
      const body = generateErrorBody(errors, moduleFields)
      dispatch(setNotification({ type: 'error', body: body }))
    }
  }

  const submitUnlock = async (lock = 1) => {
    if (lock && displayLockNotice) {
      return null;
    }
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/lock`);
    try {
      if (!lock) {
        setSaving(true)
      }
      let body = { lock: lock }
      const response = await requests.modify(body)
      if (response.status === 200) {
        setDisplayLockNotice(lock ? true : false)

        if (response.data.customer_locked) {
          setLockNotificationText('Locked by ' + response.data.lock_details.lock_agent_name + ', expiring at ' + FormatDateTimeToSame(response.data.lock_details.lock_expiration_date, 'MM/dd/yyyy hh:mm:ss a'))
          setLockAgentUserId(active.data.id)
        } else {
          setLockAgentUserId(null)
          setLockNotificationText(null)
        }
        setSaving(false)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        setSaving(false)
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const showLockNotice = () => {
    const showUnlockButton = forceRecordUnlockPermission || lockAgentUserId === active.data.id;
    return (
      <LockNotice
        content={lockNotificationText}
        showUnlockButton={showUnlockButton}
        submitUnlock={submitUnlock}
        saving={saving}
        setLockCheck={setLockCheck}
        customerId={params.id}
        setLockNotificationText={setLockNotificationText}
        setLockAgentUserId={setLockAgentUserId}
        displayLockNotice={displayLockNotice}
        lockCheck={lockCheck}
        setDisplayLockNotice={setDisplayLockNotice}
        requests={requests}
      />
    );
  }

  useEffect(() => {
    dispatchErorr()
  }, [errors, saving])

  if (modules && row) {
    const title = row['label'] != null
      ? `${row['label']} Details`
      : `${module.name} Details`

    const contentTitle = (
      <>
        {title}
        {module.helper_text && (
          <span className="ml-1 lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary" data-tooltip={module.helper_text}>
            <FontAwesomeIcon icon={faQuestionCircle} className="is-size-4 has-text-link" />
          </span>
        )}
      </>
    )

    return (
      <LayoutSidebar
        key={module.id + row.id}
        id={row.id}
        portfolioLabel={row?.portfolio_label ?? null}
        contentTitle={contentTitle}
        isLoadingBanners={isLoadingBanners}
        banners={banners}
        actionButtons={(
          <>
            <CustomerServiceButtons customer={row} module="payment_schedule" currentDate={currentDate} disableEdit={displayLockNotice && lockAgentUserId !== active.data.id} submitUnlock={submitUnlock}>
              <NoteButton />
            </CustomerServiceButtons>
            <button
              type="button"
              className="button has-background-grey has-text-white is-small header-button"
              onClick={() => {
                navi(`/customer/${customerId}`)
              }}
            >
              <span className="mx-3">Customer Details</span>
            </button>
          </>
        )}
        content={(
          <>
            {showLockNotice()}
            <div className="lox-detail columns is-multiline pt-3">
              <SubheaderDivider
                order={1}
                key="customer"
                name="Customer Information"
                toggleButton
                toggleSection={toggleSection.customer}
                onToggle={() => handleToggle('customer')}
              />
            </div>
            <div className={`lox-detail columns is-multiline pt-0 ${toggleSection.customer ? "" : "is-hidden"}`}>
              {moduleFields.map((m) => columnDataRender(m, m.id, customerFields, row))}
            </div>

            <div className="lox-detail columns is-multiline pt-3">
              <SubheaderDivider
                order={2}
                key="lead"
                name="Lead Information"
                toggleButton
                toggleSection={toggleSection.lead}
                onToggle={() => handleToggle('lead')}
              />
            </div>
            <div className={`lox-detail columns is-multiline pt-0 ${toggleSection.lead ? "" : "is-hidden"}`}>
              {leadInfo && leadInfo.id && leadModuleFields.map((l) => columnDataRender(l, l.id, leadFields, leadInfo))}
            </div>

            {leadInfo?.length == 0 && (
              <div className={`message is-white lox-dashlet-detail ${toggleSection.lead ? "" : "is-hidden"}`}>
                <div className="message-body py-0 px-1" style={{ lineHeight: 1.40 }}>
                  <NoRecord />
                </div>
              </div>
            )}

            <div className="lox-detail columns is-multiline pt-3">
              <SubheaderDivider
                order={3}
                key="loan"
                name="Loan Information"
                toggleButton
                toggleSection={toggleSection.loan}
                onToggle={() => handleToggle('loan')}
              />
            </div>
            <div className={`lox-detail columns is-multiline pt-0 ${toggleSection.loan ? "" : "is-hidden"}`}>
              {loanInfo && loanInfo.id && loanModuleFields.map((l) => columnDataRender(l, l.id, loanFields, loanInfo))}
            </div>

            {loanInfo?.length == 0 && (
              <div className={`message is-white lox-dashlet-detail ${toggleSection.loan ? "" : "is-hidden"}`}>
                <div className="message-body py-0 px-1" style={{ lineHeight: 1.40 }}>
                  <NoRecord />
                </div>
              </div>
            )}

            <div className={`lox-sub-panel-group columns is-multiline pt-3 pr-3`}>
              {loanInfo && loanInfo?.id && (
                <PaymentScheduleSubPanel
                  id={row.id}
                  hidden={editMode}
                  module={module}
                  subModule={modules.data.find((e) => e.code_key === "payment")}
                  paymentPlanPaymentModule={modules.data.find((e) => e.code_key === "payment_plan_payment")}
                  key="payment_schedule"
                  data={row}
                  setLoanInfo={setLoanInfo}
                  loanInfo={loanInfo}
                  customerLocked={displayLockNotice && lockAgentUserId !== active.data.id}
                  submitUnlock={submitUnlock}
                  currentDate={currentDate}
                  setCurrentDate={setCurrentDate}
                />
              )}

              <Suspense fallback={<div />}>
                {module?.module_related_modules?.map((m) => {
                  const subModule = modules.data.find(
                    (e) => e.code_key === m.subpanel_module_code_key
                  )
                  if (subModule && hasUserPermission(userPermissions, `${subModule.code_key}_read`)) {
                    return (
                      <ModuleSubPanel
                        id={row.id}
                        name={row?.label ? row?.label : row?.name}
                        codeKey={row?.code_key}
                        module={parentModule}
                        subModule={subModule}
                        key={m.subpanel_module_code_key}
                        hidden={editMode}
                        isPagination={m.is_pagination}
                        isOrderable={m.is_orderable}
                        isInlineEditable={m.is_inline_editable}
                        isMappingTable={m.is_mapping_table}
                        parentModuleId={params.id}
                        parentModuleData={{ ...row, loanInfo }}
                      />
                    )
                  }
                })}
              </Suspense>
            </div>
          </>
        )}
        dashlets={dashletDetailViewControl(module, row, dropdownOptions, active)}
      />
    )
  }
  return <Loading />
}
